import React, { useMemo, useState, useEffect, Fragment } from "react";
import { Descriptions, Typography, Tooltip } from "antd";
import { useIntl } from "react-intl";

import {
  getMaxLdmRule,
  sumCollisWeight,
  sumCollisVolume,
  sumNotStackableCollis,
  sumCollisLoadingMeters,
  sumCollisCalculatedWeight,
} from "../../../modules/consignmentFunctions";

const { Text } = Typography;

function ColliTableFooter({
  valid,
  rules,
  collis,
  adrCount = 0,
  onValidatonResult,
  forceLoadingMeters,
}) {
  const intl = useIntl();
  const [count, setCount] = useState();
  const [overCount, setOverCount] = useState();
  const [weight, setWeight] = useState();
  const [overWeight, setOverWeight] = useState();
  const [volume, setVolume] = useState();
  const [overVolume, setOverVolume] = useState();
  const [notStackable, setNotStackable] = useState();
  const [overNotStackable, setOverNotStackable] = useState();
  const [loadingMeters, setLoadingMeters] = useState();
  const [overLoadingMeters, setOverLoadingMeters] = useState();
  const [calculatedWeight, setCalculatedWeight] = useState();
  const [overCalculatedWeight, setOverCalculatedWeight] = useState();

  const maxLdmRule = useMemo(() => getMaxLdmRule(rules), [rules]);

  useEffect(() => {
    if (collis) {
      const isAdr = adrCount > 0;
      const actualCount = collis.length;
      setCount(actualCount);
      const oc = rules.count < actualCount || actualCount === 0;
      setOverCount(oc);
      const actualWeight = sumCollisWeight(collis);
      setWeight(actualWeight);
      const ow = rules.weight < actualWeight;
      setOverWeight(ow);
      const actualVolume = sumCollisVolume(collis);
      setVolume(actualVolume);
      const ov = rules.volume < actualVolume;
      setOverVolume(ov);
      const actualNotStackable = sumNotStackableCollis(collis);
      setNotStackable(actualNotStackable);
      const as = isAdr ? actualNotStackable === 0 : false;
      setOverNotStackable(as);
      const actualLoadingMeters = sumCollisLoadingMeters(collis, isAdr);
      setLoadingMeters(actualLoadingMeters);
      const ol = maxLdmRule < actualLoadingMeters;
      setOverLoadingMeters(ol);
      const actualCalculatedWeight = sumCollisCalculatedWeight(collis, isAdr);
      setCalculatedWeight(actualCalculatedWeight);
      const cw =
        rules.calculatedWeight &&
        rules.calculatedWeight <= actualCalculatedWeight;
      setOverCalculatedWeight(cw);
      onValidatonResult &&
        onValidatonResult(
          oc || ow || ov || as || (ol && forceLoadingMeters) ? false : true
        );
    }
  }, [
    collis,
    adrCount,
    maxLdmRule,
    rules.count,
    rules.weight,
    rules.volume,
    onValidatonResult,
    forceLoadingMeters,
    rules.calculatedWeight,
  ]);

  return !onValidatonResult || valid === false ? (
    <Descriptions
      bordered
      column={4}
      size="small"
      className="colli-table-footer"
    >
      {adrCount && (
        <Descriptions.Item
          label={intl.formatMessage({
            id: "consignment.colli.notStackableCount",
            defaultMessage: "Počet nestohovatelných kusů",
          })}
        >
          <SumItem
            max={rules.count}
            isOver={overNotStackable}
            value={notStackable}
          />
        </Descriptions.Item>
      )}
      <Descriptions.Item
        label={intl.formatMessage({
          id: "consignment.colli.count",
          defaultMessage: "Počet kusů",
        })}
      >
        <SumItem max={rules.count} isOver={overCount} value={count} />
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({
          id: "consignment.colli.weight",
          defaultMessage: "Hmotnost [kg]",
        })}
      >
        <SumItem max={rules.weight} isOver={overWeight} value={weight} />
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({
          id: "consignment.colli.volume",
          defaultMessage: "Objem [m³]",
        })}
      >
        <SumItem max={rules.volume} isOver={overVolume} value={volume} />
      </Descriptions.Item>
      {forceLoadingMeters && (
        <Descriptions.Item
          label={intl.formatMessage({
            id: "consignment.colli.loadingMeters",
            defaultMessage: "Ložné metry (LDM)",
          })}
        >
          <SumItem
            max={maxLdmRule}
            value={loadingMeters}
            isOver={overLoadingMeters}
          />
        </Descriptions.Item>
      )}
      {rules.calculatedWeight && (
        <Descriptions.Item
          label={intl.formatMessage({
            id: "consignment.colli.calculatedWeight",
            defaultMessage: "Přepočtená váha [kg]",
          })}
        >
          <SumItem
            max={rules.calculatedWeight}
            isOver={overCalculatedWeight}
            value={calculatedWeight}
          />
        </Descriptions.Item>
      )}
    </Descriptions>
  ) : (
    <Fragment />
  );
}

export const SumItem = ({ value, isOver, max }) => {
  const intl = useIntl();
  return isOver ? (
    <Tooltip
      title={
        value === 0
          ? intl.formatMessage({
              id: "consignment.colli.biggerThan0",
              defaultMessage: "Hodnota musí být větší než 0!",
            })
          : intl.formatMessage(
              {
                id: "consignment.colli.lowerThanMax",
                defaultMessage: "Překročena maximální hodnota {max}!",
              },
              { max }
            )
      }
    >
      <Text type="danger" strong>
        {value}
      </Text>
    </Tooltip>
  ) : (
    <Text>{value}</Text>
  );
};

export default ColliTableFooter;
